/**
 * Document Location Visualizer
 *
 * FERP CRITICAL: Precise source-to-field mapping with document location tracking
 * Shows exact page/line references from source document to extracted template fields
 *
 * FERP Principles:
 * - Complete traceability (field → document page/line)
 * - Data-driven (all locations from backend ChaosIngestionService)
 * - Life-critical (audit trail for regulatory compliance)
 * - Domain-agnostic (works across PDF, email, voice transcripts)
 *
 * Evidence Format:
 * - field_evidence: { [field_path]: { page: number, line: number, excerpt: string, confidence: number } }
 * - Document location: Page number + line number + highlighted excerpt
 * - Multi-LLM consensus: Shows agreement across GPT-4, Claude, Gemini
 */
// @ts-nocheck


import React, { useState, useMemo } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Paper,
  ScrollArea,
  Table,
  Tooltip,
  ActionIcon,
  ThemeIcon,
  Highlight,
  Accordion,
  Code,
  Alert,
  Tabs,
  Progress,
  RingProgress,
  Center,
} from '@mantine/core';
import {
  IconFileText,
  IconMapPin,
  IconExternalLink,
  IconZoomIn,
  IconCheck,
  IconAlertCircle,
  IconBrain,
  IconUsers,
} from '@tabler/icons-react';

/**
 * Document location interface
 */
export interface DocumentLocation {
  page: number;
  line: number;
  excerpt: string;
  confidence: number;
  source?: string; // LLM source (GPT-4, Claude, Gemini)
}

/**
 * Field evidence with location tracking
 */
export interface FieldEvidence {
  field_path: string;
  field_label?: string;
  extracted_value: any;
  locations: DocumentLocation[];
  consensus_score: number; // Multi-LLM agreement score
  primary_source?: DocumentLocation; // Highest confidence location
}

/**
 * Multi-LLM comparison data
 */
export interface MultiLLMComparison {
  llm_name: string; // GPT-4, Claude, Gemini
  confidence: number;
  location: DocumentLocation;
  agrees_with_consensus: boolean;
}

export interface DocumentLocationVisualizerProps {
  fieldEvidence: Record<string, any>; // field_evidence from ConversionResult
  documentUrl?: string; // S3 URL for PDF preview
  onLocationClick?: (location: DocumentLocation) => void;
}

/**
 * Document Location Visualizer Component
 *
 * FERP Pattern: Precise source-to-field traceability with page/line references
 */
export function DocumentLocationVisualizer({
  fieldEvidence,
  documentUrl,
  onLocationClick,
}: DocumentLocationVisualizerProps): JSX.Element {
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('locations');

  /**
   * Parse field evidence into structured format
   */
  const parsedEvidence = useMemo((): FieldEvidence[] => {
    if (!fieldEvidence || typeof fieldEvidence !== 'object') return [];

    return Object.entries(fieldEvidence).map(([fieldPath, data]: [string, any]) => {
      // Extract locations from evidence data
      const locations: DocumentLocation[] = [];

      if (data.location) {
        locations.push({
          page: data.location.page || 1,
          line: data.location.line || 1,
          excerpt: data.location.excerpt || data.excerpt || '',
          confidence: data.confidence || 0,
          source: data.source || 'primary',
        });
      }

      // Multi-LLM evidence
      if (data.llm_evidence && Array.isArray(data.llm_evidence)) {
        data.llm_evidence.forEach((llmData: any) => {
          locations.push({
            page: llmData.page || 1,
            line: llmData.line || 1,
            excerpt: llmData.excerpt || '',
            confidence: llmData.confidence || 0,
            source: llmData.llm_name || 'unknown',
          });
        });
      }

      // Find highest confidence location
      const primarySource = locations.reduce((best, current) =>
        current.confidence > best?.confidence ? current : best,
        locations[0]
      );

      // Calculate consensus score (average confidence)
      const consensusScore = locations.length > 0
        ? locations.reduce((sum, loc) => sum + loc.confidence, 0) / locations.length
        : 0;

      return {
        field_path: fieldPath,
        field_label: data.label || fieldPath,
        extracted_value: data.value || data.extracted_value,
        locations,
        consensus_score: consensusScore,
        primary_source: primarySource,
      };
    });
  }, [fieldEvidence]);

  /**
   * Get confidence color
   */
  const getConfidenceColor = (score: number): string => {
    if (score >= 0.9) return 'green';
    if (score >= 0.8) return 'blue';
    if (score >= 0.7) return 'yellow';
    return 'red';
  };

  /**
   * Get consensus color
   */
  const getConsensusColor = (score: number): string => {
    if (score >= 0.85) return 'teal';
    if (score >= 0.7) return 'blue';
    return 'orange';
  };

  /**
   * Handle location click
   */
  const handleLocationClick = (location: DocumentLocation): void => {
    onLocationClick?.(location);
    // Could open PDF viewer at specific page/line
  };

  /**
   * Render location card
   */
  const renderLocationCard = (location: DocumentLocation, index: number): JSX.Element => (
    <Paper key={index} p="sm" withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" color={getConfidenceColor(location.confidence)}>
            <IconMapPin size={14} />
          </ThemeIcon>
          <Text size="sm" fw={500}>
            Page {location.page}, Line {location.line}
          </Text>
          {location.source && (
            <Badge size="sm" variant="outline">
              {location.source}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Badge color={getConfidenceColor(location.confidence)}>
            {(location.confidence * 100).toFixed(0)}%
          </Badge>
          <ActionIcon
            size="sm"
            variant="subtle"
            onClick={() => handleLocationClick(location)}
          >
            <IconExternalLink size={14} />
          </ActionIcon>
        </Group>
      </Group>
      <Code block color="gray" style={{ fontSize: '0.75rem' }}>
        {location.excerpt}
      </Code>
    </Paper>
  );

  /**
   * Render multi-LLM comparison
   */
  const renderMultiLLMComparison = (field: FieldEvidence): JSX.Element => {
    const llmComparisons: MultiLLMComparison[] = field.locations.map(loc => ({
      llm_name: loc.source || 'Unknown',
      confidence: loc.confidence,
      location: loc,
      agrees_with_consensus: Math.abs(loc.confidence - field.consensus_score) < 0.1,
    }));

    return (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Multi-LLM Consensus Analysis
        </Text>
        <Table fz="xs">
          <thead>
            <tr>
              <th>LLM</th>
              <th>Confidence</th>
              <th>Location</th>
              <th>Consensus</th>
            </tr>
          </thead>
          <tbody>
            {llmComparisons.map((comp, idx) => (
              <tr key={idx}>
                <td>
                  <Group gap="xs">
                    <ThemeIcon size="xs" color="blue">
                      <IconBrain size={12} />
                    </ThemeIcon>
                    <Text size="xs">{comp.llm_name}</Text>
                  </Group>
                </td>
                <td>
                  <Badge size="sm" color={getConfidenceColor(comp.confidence)}>
                    {(comp.confidence * 100).toFixed(0)}%
                  </Badge>
                </td>
                <td>
                  <Text size="xs">
                    Page {comp.location.page}, Line {comp.location.line}
                  </Text>
                </td>
                <td>
                  <ThemeIcon
                    size="xs"
                    color={comp.agrees_with_consensus ? 'green' : 'orange'}
                  >
                    {comp.agrees_with_consensus ? (
                      <IconCheck size={12} />
                    ) : (
                      <IconAlertCircle size={12} />
                    )}
                  </ThemeIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Stack>
    );
  };

  /**
   * Render field evidence accordion
   */
  const renderFieldAccordion = (): JSX.Element => (
    <Accordion
      value={selectedField}
      onChange={setSelectedField}
      variant="separated"
    >
      {parsedEvidence.map((field, idx) => (
        <Accordion.Item key={idx} value={field.field_path}>
          <Accordion.Control>
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  {field.field_label}
                </Text>
                <Text size="xs" color="dimmed">
                  ({field.field_path})
                </Text>
              </Group>
              <Group gap="xs">
                <Badge color={getConsensusColor(field.consensus_score)}>
                  Consensus: {(field.consensus_score * 100).toFixed(0)}%
                </Badge>
                <Badge variant="outline">
                  {field.locations.length} source{field.locations.length !== 1 ? 's' : ''}
                </Badge>
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              {/* Extracted value */}
              <Paper p="sm" withBorder>
                <Text size="xs" color="dimmed" mb={4}>
                  Extracted Value
                </Text>
                <Text size="sm" fw={500}>
                  {typeof field.extracted_value === 'object'
                    ? JSON.stringify(field.extracted_value)
                    : String(field.extracted_value)}
                </Text>
              </Paper>

              {/* Primary source location */}
              {field.primary_source && (
                <div>
                  <Text size="xs" color="dimmed" mb={4}>
                    Primary Source (Highest Confidence)
                  </Text>
                  {renderLocationCard(field.primary_source, -1)}
                </div>
              )}

              {/* All locations */}
              {field.locations.length > 1 && (
                <div>
                  <Text size="xs" color="dimmed" mb={4}>
                    All Source Locations ({field.locations.length})
                  </Text>
                  <Stack gap="xs">
                    {field.locations.map((loc, locIdx) => renderLocationCard(loc, locIdx))}
                  </Stack>
                </div>
              )}

              {/* Multi-LLM comparison */}
              {field.locations.length > 1 && renderMultiLLMComparison(field)}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  /**
   * Render evidence statistics
   */
  const renderStatistics = (): JSX.Element => {
    const totalFields = parsedEvidence.length;
    const highConfidenceFields = parsedEvidence.filter(f => f.consensus_score >= 0.85).length;
    const multiSourceFields = parsedEvidence.filter(f => f.locations.length > 1).length;
    const avgConfidence = parsedEvidence.reduce((sum, f) => sum + f.consensus_score, 0) / totalFields;

    return (
      <Group grow>
        <Card withBorder>
          <Center>
            <RingProgress
              size={120}
              thickness={12}
              sections={[
                { value: (highConfidenceFields / totalFields) * 100, color: 'green' },
              ]}
              label={
                <Center>
                  <Stack gap={0} ta="center">
                    <Text size="lg" fw={700}>
                      {highConfidenceFields}
                    </Text>
                    <Text size="xs" color="dimmed">
                      High Confidence
                    </Text>
                  </Stack>
                </Center>
              }
            />
          </Center>
        </Card>

        <Card withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Total Fields
              </Text>
              <Badge>{totalFields}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Multi-Source
              </Text>
              <Badge color="blue">{multiSourceFields}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                Avg Confidence
              </Text>
              <Badge color={getConfidenceColor(avgConfidence)}>
                {(avgConfidence * 100).toFixed(0)}%
              </Badge>
            </Group>
          </Stack>
        </Card>
      </Group>
    );
  };

  if (parsedEvidence.length === 0) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="No Evidence Available" color="yellow">
        No field evidence data available for visualization. Evidence attribution requires
        document location tracking from the Chaos Ingestion Service.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <ThemeIcon color="blue">
              <IconFileText size={20} />
            </ThemeIcon>
            <Text size="lg" fw={700}>
              Document Location Tracking
            </Text>
          </Group>
          {documentUrl && (
            <ActionIcon component="a" href={documentUrl} target="_blank" variant="subtle">
              <IconZoomIn size={18} />
            </ActionIcon>
          )}
        </Group>

        <Text size="sm" color="dimmed" mb="md">
          Precise source-to-field traceability with page/line references. All locations
          extracted by Chaos Ingestion Service with multi-LLM consensus validation.
        </Text>

        {renderStatistics()}
      </Card>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="locations" leftSection={<IconMapPin size={14} />}>
            Field Locations
          </Tabs.Tab>
          <Tabs.Tab value="consensus" leftSection={<IconUsers size={14} />}>
            Multi-LLM Consensus
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="locations" pt="md">
          {renderFieldAccordion()}
        </Tabs.Panel>

        <Tabs.Panel value="consensus" pt="md">
          <Stack gap="md">
            {parsedEvidence
              .filter(f => f.locations.length > 1)
              .map((field, idx) => (
                <Card key={idx} withBorder>
                  <Text size="sm" fw={500} mb="xs">
                    {field.field_label}
                  </Text>
                  {renderMultiLLMComparison(field)}
                </Card>
              ))}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
